import React, { useEffect, useState } from "react";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Badge,
  Spinner,
  Avatar,
  Tooltip,
} from "@nextui-org/react";
import { FaBell, FaShareNodes, FaTrash, FaCheck } from "react-icons/fa6";
import { apiFetch } from "../../utils/authFetch";
import { API_URL } from "../../config/api";
import { renderRichText } from "../../utils/richText";

function absoluteUpload(url) {
  if (!url) return null;
  return url.startsWith("http") ? url : `${API_URL}${url}`;
}

// Strips markdown/HTML down to a plain-text one-liner for the list preview.
function snippet(text, format) {
  const plain = renderRichText(text, format)
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return plain.length > 90 ? `${plain.slice(0, 90)}…` : plain;
}

function timeAgo(iso) {
  const diff = (Date.now() - new Date(`${iso}Z`).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [list, setList] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(null); // announcement being viewed in the modal
  const [shareMsg, setShareMsg] = useState("");

  const loadUnread = () => {
    apiFetch("/api/announcements/unread-count")
      .then((d) => setUnread(d.count))
      .catch(() => {});
  };

  const loadList = () => {
    setLoading(true);
    apiFetch("/api/announcements")
      .then((d) => setList(d.announcements))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadUnread();
    const interval = setInterval(loadUnread, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (open) loadList();
  }, [open]);

  const openAnnouncement = async (a) => {
    setShareMsg("");
    try {
      const d = await apiFetch(`/api/announcements/${a.id}`);
      setActive(d.announcement);
      setList((prev) => prev.map((x) => (x.id === a.id ? { ...x, is_read: true } : x)));
      setUnread((u) => (a.is_read ? u : Math.max(0, u - 1)));
    } catch {
      // ignore, keep the list open
    }
  };

  const markRead = async (a, e) => {
    e.stopPropagation();
    if (a.is_read) return;
    try {
      await apiFetch(`/api/announcements/${a.id}/read`, { method: "POST" });
      setList((prev) => prev.map((x) => (x.id === a.id ? { ...x, is_read: true } : x)));
      setUnread((u) => Math.max(0, u - 1));
    } catch {
      // best effort
    }
  };

  const dismiss = async (a, e) => {
    if (e) e.stopPropagation();
    try {
      await apiFetch(`/api/announcements/${a.id}`, { method: "DELETE" });
      setList((prev) => prev.filter((x) => x.id !== a.id));
      if (!a.is_read) setUnread((u) => Math.max(0, u - 1));
      if (active && active.id === a.id) setActive(null);
    } catch {
      // best effort
    }
  };

  const share = async (a) => {
    try {
      const d = await apiFetch(`/api/announcements/${a.id}/share`, { method: "POST" });
      if (navigator.share) {
        await navigator.share({ title: a.title, url: d.url }).catch(() => {});
      } else {
        await navigator.clipboard.writeText(d.url);
        setShareMsg("Link copied to clipboard ✨");
        setTimeout(() => setShareMsg(""), 2500);
      }
    } catch {
      // best effort
    }
  };

  return (
    <>
      <Popover placement="bottom-end" isOpen={open} onOpenChange={setOpen}>
        <PopoverTrigger>
          <button
            type="button"
            aria-label="Notifications"
            className="relative flex items-center justify-center w-9 h-9 rounded-full hover:bg-white/10 transition-colors"
          >
            <Badge
              content={unread > 9 ? "9+" : unread}
              color="danger"
              size="sm"
              isInvisible={unread === 0}
              shape="circle"
            >
              <FaBell size={18} className="text-foreground-400" />
            </Badge>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[340px] p-0 overflow-hidden">
          <div className="w-full">
            <div className="px-4 py-3 border-b border-default-100 flex items-center justify-between">
              <span className="font-semibold bg-gradient-to-r from-pink-400 to-yellow-300 bg-clip-text text-transparent">
                📣 Announcements
              </span>
              {unread > 0 && <span className="text-tiny text-foreground-400">{unread} new</span>}
            </div>
            <div className="max-h-[420px] overflow-y-auto">
              {loading && (
                <div className="flex justify-center py-6">
                  <Spinner size="sm" />
                </div>
              )}
              {!loading && list.length === 0 && (
                <p className="text-center text-foreground-400 text-small py-6 px-4">
                  Nothing here yet — check back soon! 🐾
                </p>
              )}
              {!loading &&
                list.map((a) => (
                  <button
                    type="button"
                    key={a.id}
                    onClick={() => openAnnouncement(a)}
                    className="w-full text-left px-4 py-3 border-b border-default-50 hover:bg-white/5 transition-colors flex flex-col gap-1"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <span className="flex items-center gap-1.5 font-medium text-small">
                        {!a.is_read && <span className="w-1.5 h-1.5 rounded-full bg-danger shrink-0" />}
                        {a.title}
                      </span>
                      <span className="text-tiny text-foreground-400 shrink-0">{timeAgo(a.created_at)}</span>
                    </div>
                    <p className="text-tiny text-foreground-400 line-clamp-2">{snippet(a.body, a.format)}</p>
                    <div className="flex items-center gap-1 mt-1">
                      {!a.is_read && (
                        <Tooltip content="Mark as read" size="sm">
                          <span
                            role="button"
                            tabIndex={0}
                            onClick={(e) => markRead(a, e)}
                            className="p-1.5 rounded-full hover:bg-white/10 text-foreground-400 hover:text-success"
                          >
                            <FaCheck size={11} />
                          </span>
                        </Tooltip>
                      )}
                      <Tooltip content="Delete for me" size="sm">
                        <span
                          role="button"
                          tabIndex={0}
                          onClick={(e) => dismiss(a, e)}
                          className="p-1.5 rounded-full hover:bg-white/10 text-foreground-400 hover:text-danger"
                        >
                          <FaTrash size={11} />
                        </span>
                      </Tooltip>
                    </div>
                  </button>
                ))}
            </div>
          </div>
        </PopoverContent>
      </Popover>

      <Modal isOpen={!!active} onOpenChange={(v) => !v && setActive(null)} backdrop="blur" size="lg" scrollBehavior="inside">
        <ModalContent>
          {(onClose) => active && (
            <>
              <ModalHeader className="flex flex-col gap-1 pb-2">
                <span className="text-tiny uppercase tracking-wide text-pink-400 font-semibold">📣 Announcement</span>
                <span className="text-xl">{active.title}</span>
              </ModalHeader>
              <ModalBody>
                <div className="flex items-center gap-2 -mt-2 mb-1">
                  <Avatar src={absoluteUpload(active.author?.avatar_url)} name={active.author?.nickname} size="sm" />
                  <span className="text-small text-foreground-400">
                    {active.author?.display_name || `@${active.author?.nickname}`} &middot; {timeAgo(active.created_at)}
                  </span>
                </div>
                <div
                  className="prose prose-invert max-w-none"
                  dangerouslySetInnerHTML={{ __html: renderRichText(active.body, active.format) }}
                />
                {shareMsg && <p className="text-success text-small">{shareMsg}</p>}
              </ModalBody>
              <ModalFooter className="flex items-center justify-between">
                <Button variant="light" color="danger" startContent={<FaTrash size={12} />} onPress={() => dismiss(active)}>
                  Delete for me
                </Button>
                <div className="flex gap-2">
                  <Button variant="flat" startContent={<FaShareNodes size={13} />} onPress={() => share(active)}>
                    Share
                  </Button>
                  <Button color="primary" onPress={onClose}>
                    Close
                  </Button>
                </div>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
}
